import React, { useState } from "react";
import {
  Stack,
  Group,
  Button,
  Text,
  Table,
  ActionIcon,
  Alert,
} from "@mantine/core";
import { IconGripVertical, IconAlertCircle } from "@tabler/icons-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  Modifier,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useApp } from "../../context/AppContext";
import { Employee } from "../../types/salon";
import { StaffAvatar } from "../common/StaffAvatar";
import { StandardTableShell } from "../common/StandardTable/StandardTable";
import styles from "./StaffReorderPanel.module.css";

// Lock dragging to the vertical axis (no @dnd-kit/modifiers dependency), so a
// row can't drift sideways out of the table. Mirrors StaffTiersSettings.
const lockToVerticalAxis: Modifier = ({ transform }) => ({
  ...transform,
  x: 0,
});

const fullName = (e: Employee): string =>
  `${e.first_name} ${e.last_name}`.trim();

const roleLine = (e: Employee): string =>
  e.title ? `${e.role}, ${e.title}` : e.role;

/** A single draggable staff row, shown only while reordering. */
const ReorderRow: React.FC<{ employee: Employee }> = ({ employee }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: employee.id });
  return (
    <Table.Tr
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        position: "relative",
        zIndex: isDragging ? 1 : undefined,
        background: isDragging
          ? "var(--mantine-color-gray-0)"
          : "var(--mantine-color-body)",
      }}
    >
      <Table.Td w={52}>
        <ActionIcon
          variant="subtle"
          color="gray"
          aria-label={`Drag ${fullName(employee)}`}
          className={styles.handle}
          {...attributes}
          {...listeners}
        >
          <IconGripVertical size={18} />
        </ActionIcon>
      </Table.Td>
      <Table.Td>
        <Group gap="sm" wrap="nowrap" miw={0}>
          <StaffAvatar
            photoUrl={employee.staff_photo_url ?? employee.avatar_url}
            firstName={employee.first_name}
            lastName={employee.last_name}
            size={32}
            radius={32}
          />
          <Text fw={500} truncate>
            {fullName(employee)}
          </Text>
        </Group>
      </Table.Td>
      <Table.Td>
        <Text c="dimmed" size="sm">
          {roleLine(employee)}
        </Text>
      </Table.Td>
    </Table.Tr>
  );
};

interface StaffReorderPanelProps {
  /** Active staff to reorder, in their current display order. */
  employees: Employee[];
  salonId: string;
  onCancel: () => void;
  /** Called after a successful save so the parent can refresh the roster
   * (the backend re-sorts GET staff by the new order) and exit reorder mode. */
  onSaved: () => Promise<void> | void;
}

/**
 * Inline drag-and-drop table for setting the salon's staff display order
 * (Staff Settings "Edit Order"). Mirrors the Staff Tiers reorder pattern: a
 * local draft that only commits on Save. The persisted order drives both this
 * list and the calendar columns.
 */
const StaffReorderPanel: React.FC<StaffReorderPanelProps> = ({
  employees,
  salonId,
  onCancel,
  onSaved,
}) => {
  const { reorderStaff } = useApp();
  const [order, setOrder] = useState<Employee[]>(() => [...employees]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const onDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = order.findIndex((e) => e.id === active.id);
    const to = order.findIndex((e) => e.id === over.id);
    if (from === -1 || to === -1) return;
    setOrder(arrayMove(order, from, to));
  };

  const save = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      await reorderStaff(salonId, {
        order: order.map((e, i) => ({ id: e.id, sort_order: i })),
      });
      await onSaved();
    } catch (err) {
      console.error("Error reordering staff:", err);
      setError("Couldn't save the new order. Please try again.");
      setSaving(false);
    }
  };

  return (
    <Stack gap="md">
      <Text c="dimmed" size="sm">
        Drag staff to set the order they appear in here and as columns on the
        calendar.
      </Text>

      {error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          withCloseButton
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <StandardTableShell minWidth={420} ariaLabel="Reorder staff">
        <Table.Tbody>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onDragEnd}
            modifiers={[lockToVerticalAxis]}
          >
            <SortableContext
              items={order.map((e) => e.id)}
              strategy={verticalListSortingStrategy}
            >
              {order.map((employee) => (
                <ReorderRow key={employee.id} employee={employee} />
              ))}
            </SortableContext>
          </DndContext>
        </Table.Tbody>
      </StandardTableShell>

      <Group justify="flex-end" gap="xs">
        <Button variant="default" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button color="var(--brand-teal)" onClick={save} loading={saving}>
          Save order
        </Button>
      </Group>
    </Stack>
  );
};

export default StaffReorderPanel;
